/**
 * Client-side attribution capture & persistence.
 * Persists via cookie + localStorage + DB so we never lose ownership.
 */
import { getSessionId } from "@/lib/session";
import { resolveTouchFromUrl } from "./attributionPriority";
import { captureAttributionTouch, recordAttributionEvent } from "./attribution.functions";
import type { AttributionTouch } from "./attributionTypes";

const LS_KEY = "vs:attribution:v1";
const COOKIE = "vs_attr";
const COOKIE_DAYS = 90;

interface PersistedAttribution extends AttributionTouch {
  capturedAt: string;
}

function setCookie(value: string) {
  if (typeof document === "undefined") return;
  const exp = new Date(Date.now() + COOKIE_DAYS * 86400 * 1000).toUTCString();
  document.cookie = `${COOKIE}=${encodeURIComponent(value)}; expires=${exp}; path=/; SameSite=Lax`;
}

function readCookie(): string | null {
  if (typeof document === "undefined") return null;
  const m = document.cookie.match(new RegExp(`(?:^|; )${COOKIE}=([^;]+)`));
  return m ? decodeURIComponent(m[1]) : null;
}

export function readPersistedAttribution(): PersistedAttribution | null {
  if (typeof window === "undefined") return null;
  try {
    const raw = localStorage.getItem(LS_KEY) ?? readCookie();
    if (!raw) return null;
    return JSON.parse(raw) as PersistedAttribution;
  } catch { return null; }
}

function persist(touch: AttributionTouch) {
  const payload: PersistedAttribution = { ...touch, capturedAt: new Date().toISOString() };
  const json = JSON.stringify(payload);
  try { localStorage.setItem(LS_KEY, json); } catch { /* ignore */ }
  setCookie(json);
}

/** Boot capture — call once at app load. */
export function initAttributionEngine(): void {
  if (typeof window === "undefined") return;
  const touch = resolveTouchFromUrl(window.location.search);
  if (touch) {
    persist(touch);
    void captureAttributionTouch({
      data: { ...touch, sessionId: getSessionId() },
    }).catch(() => {});
    return;
  }
  // No new URL touch — refresh existing attribution so last_active_at extends
  const existing = readPersistedAttribution();
  if (existing) {
    void captureAttributionTouch({
      data: { ...existing, sessionId: getSessionId() },
    }).catch(() => {});
  }
}

/** Bind attribution to authenticated user after sign-in. */
export function attachUserToAttribution(userId: string): void {
  const existing = readPersistedAttribution();
  if (!existing) return;
  void captureAttributionTouch({
    data: { ...existing, sessionId: getSessionId(), userId },
  }).catch(() => {});
}

/** Convenience wrappers for app event hooks. */
export function trackAttributionEvent(
  eventType:
    | "signup" | "first_deposit" | "deposit" | "betting_activity"
    | "engagement" | "sponsor_interaction" | "qr_scan"
    | "campaign_entry" | "creator_referral",
  eventData?: Record<string, unknown>,
  userId?: string | null,
): void {
  void recordAttributionEvent({
    data: {
      sessionId: getSessionId(),
      userId: userId ?? null,
      eventType,
      eventData: eventData ?? {},
    },
  }).catch(() => {});
}